import { useEffect, useMemo, useRef, useState } from "react";
import { Search, Plus, Minus, Trash2, Tag, BadgePercent, ScanBarcode, Loader2, AlertTriangle, Pill } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr, peso } from "../lib/api";
import { queueSale, getOutbox, setOutbox, cacheProducts, getCachedProducts } from "../lib/offline";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import ReceiptDialog from "../components/ReceiptDialog";
import { NeedTenant } from "../components/layout/AppShell";

export default function POSPage() {
  const { user, tenant, activeTenantId } = useAuth();
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [cart, setCart] = useState([]);
  const [discount, setDiscount] = useState({ type: "amount", value: 0 });
  const [discountOpen, setDiscountOpen] = useState(false);
  const [discountDraft, setDiscountDraft] = useState({ type: "amount", value: "" });
  const [seniorPwd, setSeniorPwd] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [tendered, setTendered] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [rxOpen, setRxOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [buyerId, setBuyerId] = useState("");
  const [buyerForm, setBuyerForm] = useState({ name: "", tin: "", address: "", business_name: "" });
  const [taxCfg, setTaxCfg] = useState(null);
  const [taxTreatment, setTaxTreatment] = useState("vatable");
  const [offline, setOffline] = useState(!navigator.onLine);
  const [outboxCount, setOutboxCount] = useState(getOutbox().length);
  const [ewRef, setEwRef] = useState("");
  const searchRef = useRef(null);
  const bir = tenant?.bir_compliance_enabled;

  const syncOutbox = async () => {
    const outbox = getOutbox();
    if (!outbox.length || !navigator.onLine) return;
    let ok = 0, failed = 0;
    for (const s of outbox) {
      try { await api.post("/sales", s.payload); ok++; }
      catch { failed++; }
    }
    setOutbox(failed ? outbox.slice(ok) : []);
    setOutboxCount(failed ? outbox.length - ok : 0);
    if (ok) toast.success(`Synced ${ok} offline sale${ok > 1 ? "s" : ""}`);
    if (failed) toast.error(`${failed} offline sale(s) failed to sync`);
    load();
  };

  const load = async () => {
    try {
      const { data } = await api.get("/products");
      setProducts(data);
      cacheProducts(data);
      setOffline(false);
      api.get("/customers").then(({ data: c }) => setCustomers(c)).catch(() => {});
      api.get("/bir/tax-config").then(({ data: tc }) => setTaxCfg(tc)).catch(() => {});
    } catch (e) {
      const cached = getCachedProducts();
      if (cached.length) {
        setProducts(cached);
        setOffline(true);
        toast.warning("Offline mode — showing cached catalog. Sales will queue and sync when back online.");
      } else {
        toast.error(fmtErr(e));
      }
    }
  };
  useEffect(() => {
    if (user.role === "system_admin" && !activeTenantId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenantId]);

  useEffect(() => {
    const onOnline = () => { setOffline(false); syncOutbox(); };
    const onOffline = () => setOffline(true);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => { window.removeEventListener("online", onOnline); window.removeEventListener("offline", onOffline); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categories = useMemo(() => ["all", ...new Set(products.map((p) => p.category).filter(Boolean))], [products]);
  const filtered = useMemo(() => products.filter((p) => {
    const q = search.trim().toLowerCase();
    const matchQ = !q || p.name.toLowerCase().includes(q) || (p.sku_barcode || "").toLowerCase().includes(q);
    const matchC = category === "all" || p.category === category;
    return matchQ && matchC;
  }), [products, search, category]);

  const addToCart = (p) => {
    if (p.is_expired) { toast.error(`"${p.name}" is EXPIRED and blocked from sale`); return; }
    setCart((prev) => {
      const ex = prev.find((c) => c.product_id === p.product_id);
      const qty = (ex?.quantity || 0) + 1;
      if (qty > p.stock_quantity) { toast.error(`Only ${p.stock_quantity} in stock`); return prev; }
      if (ex) return prev.map((c) => c.product_id === p.product_id ? { ...c, quantity: qty } : c);
      return [...prev, { product_id: p.product_id, name: p.name, unit_price: p.retail_price, quantity: 1, stock: p.stock_quantity, requires_rx: !!(p.generic_metadata || {}).requires_rx }];
    });
  };

  const onSearchKey = (e) => {
    if (e.key !== "Enter") return;
    const q = search.trim().toLowerCase();
    if (!q) return;
    const exact = products.find((p) => (p.sku_barcode || "").toLowerCase() === q);
    const target = exact || (filtered.length === 1 ? filtered[0] : null);
    if (target) { addToCart(target); setSearch(""); }
  };

  const setQty = (id, qty) => {
    setCart((prev) => {
      const item = prev.find((c) => c.product_id === id);
      if (!item) return prev;
      if (qty <= 0) return prev.filter((c) => c.product_id !== id);
      if (qty > item.stock) { toast.error(`Only ${item.stock} in stock`); return prev; }
      return prev.map((c) => c.product_id === id ? { ...c, quantity: qty } : c);
    });
  };

  const totals = useMemo(() => {
    const subtotal = cart.reduce((s, c) => s + c.unit_price * c.quantity, 0);
    const discountAmount = discount.value > 0
      ? (discount.type === "percent" ? subtotal * Math.min(discount.value, 100) / 100 : Math.min(discount.value, subtotal))
      : 0;
    const net = subtotal - discountAmount;
    const rate = taxCfg?.vat_rate ?? 0.12;
    const treatment = seniorPwd ? "vat_exempt" : taxTreatment;
    let vatable = 0, vat = 0, exempt = 0, zero = 0, seniorDiscount = 0, total = net;
    if (bir) {
      const base = rate ? net / (1 + rate) : net;
      if (treatment === "vat_exempt") {
        exempt = base;
        if (seniorPwd) seniorDiscount = base * (taxCfg?.senior_discount_rate ?? 0.2);
        total = base - seniorDiscount;
      } else if (treatment === "zero_rated") {
        zero = net;
        total = net;
      } else {
        vatable = base;
        vat = base * rate;
        total = net;
      }
    }
    return { subtotal, discountAmount, net, vatable, vat, exempt, zero, seniorDiscount, total, treatment };
  }, [cart, discount, bir, seniorPwd, taxCfg, taxTreatment]);

  const completeOrder = async () => {
    const buyer = buyerId
      ? { customer_id: buyerId, name: customers.find((c) => c.customer_id === buyerId) ? `${customers.find((c) => c.customer_id === buyerId).first_name} ${customers.find((c) => c.customer_id === buyerId).last_name}` : null, ...Object.fromEntries(Object.entries(buyerForm).filter(([, v]) => v)) }
      : (Object.values(buyerForm).some(Boolean) ? Object.fromEntries(Object.entries(buyerForm).filter(([, v]) => v)) : null);
    const payload = {
      items: cart.map((c) => ({ product_id: c.product_id, quantity: c.quantity })),
      discount, payment_method: paymentMethod,
      tendered: paymentMethod === "cash" && tendered ? Number(tendered) : null,
      senior_pwd: seniorPwd, tax_treatment: totals.treatment,
      payment_mode: paymentMethod === "ewallet" ? "manual" : null,
      payment_ref: paymentMethod === "ewallet" ? ewRef : null,
      buyer,
    };
    if (offline || !navigator.onLine) {
      queueSale({ payload, queued_at: new Date().toISOString() });
      setOutboxCount(getOutbox().length);
      setReceipt({
        invoice_number: `OFFLINE-${Date.now().toString().slice(-6)}`, cashier_name: user.name,
        created_at: new Date().toISOString(), items: cart.map((c) => ({ sale_item_id: c.product_id, name: c.name, quantity: c.quantity, subtotal: c.unit_price * c.quantity })),
        subtotal: totals.subtotal, discount_amount: totals.discountAmount, total_amount: totals.total,
        payment_method: paymentMethod, tendered: tendered ? Number(tendered) : null,
        change: paymentMethod === "cash" && tendered ? Number(tendered) - totals.total : null,
        bir_compliant: false, status: "PAID",
      });
      setCart([]); setDiscount({ type: "amount", value: 0 }); setSeniorPwd(false);
      setCheckoutOpen(false); setTendered(""); setBuyerId(""); setBuyerForm({ name: "", tin: "", address: "", business_name: "" });
      toast.warning("Offline — sale queued and will sync when back online");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/sales", payload);
      setReceipt(data);
      setCart([]); setDiscount({ type: "amount", value: 0 }); setSeniorPwd(false);
      setCheckoutOpen(false); setTendered(""); setBuyerId(""); setBuyerForm({ name: "", tin: "", address: "", business_name: "" });
      setEwRef("");
      toast.success(`Sale ${data.invoice_number} completed`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setSubmitting(false); }
  };

  const change = paymentMethod === "cash" && tendered ? Number(tendered) - totals.total : 0;
  const lowStock = useMemo(() => products.filter((p) => p.is_low_stock), [products]);
  const rxItems = cart.filter((c) => c.requires_rx);
  const handleCharge = () => {
    if (rxItems.length > 0) setRxOpen(true);
    else setCheckoutOpen(true);
  };

  if (user.role === "system_admin" && !activeTenantId) return <NeedTenant />;

  return (
    <div className="grid lg:grid-cols-5 gap-6" data-testid="pos-terminal">
      <div className="lg:col-span-3 space-y-4">
        <div className="relative">
          <ScanBarcode className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            ref={searchRef}
            data-testid="pos-search-barcode-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={onSearchKey}
            placeholder="Scan barcode or search products… (Enter to add)"
            className="pl-9 h-12 text-base bg-white"
            autoFocus
          />
        </div>
        {lowStock.length > 0 && (
          <div className="flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3" data-testid="pos-low-stock-banner">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-800 leading-relaxed">
              <span className="font-bold">{lowStock.length} item{lowStock.length > 1 ? "s" : ""} low on stock:</span>{" "}
              {lowStock.slice(0, 5).map((p) => `${p.name} (${p.stock_quantity})`).join(", ")}{lowStock.length > 5 ? "…" : ""}
            </p>
          </div>
        )}
        {(offline || outboxCount > 0) && (
          <div className="flex items-start gap-2.5 rounded-lg border border-slate-300 bg-slate-100 px-4 py-3" data-testid="pos-offline-banner">
            <AlertTriangle className="h-4 w-4 text-slate-600 mt-0.5 shrink-0" />
            <p className="text-xs text-slate-700 leading-relaxed">
              {offline ? "You are offline — new sales will be queued locally. " : ""}
              {outboxCount > 0 ? `${outboxCount} sale${outboxCount > 1 ? "s" : ""} waiting to sync.` : ""}
            </p>
          </div>
        )}
        <div className="flex gap-2 flex-wrap">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={c === "all" ? "pos-category-filter-all" : `pos-category-${c}`}
              onClick={() => setCategory(c)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-medium capitalize transition-colors ${
                category === c ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
          {filtered.map((p) => (
            <button
              key={p.product_id}
              data-testid="pos-product-card"
              onClick={() => addToCart(p)}
              disabled={p.is_expired}
              className={`relative min-h-[130px] p-3 rounded-xl border text-left flex flex-col justify-between transition-all ${
                p.is_expired
                  ? "border-red-200 bg-red-50 cursor-not-allowed opacity-80"
                  : "border-slate-200 bg-white hover:border-slate-400 hover:shadow-sm cursor-pointer"
              }`}
            >
              {p.is_expired && (
                <span className="absolute top-2 right-2 rounded bg-red-100 border border-red-200 px-1.5 py-0.5 text-[10px] font-bold text-red-700">EXPIRED</span>
              )}
              {!p.is_expired && p.is_expiring_soon && (
                <span className="absolute top-2 right-2 rounded bg-orange-100 border border-orange-200 px-1.5 py-0.5 text-[10px] font-bold text-orange-700">EXPIRING</span>
              )}
              <div className="space-y-1 pr-12">
                <p className="text-sm font-semibold text-slate-900 leading-snug line-clamp-2">{p.name}</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{p.sku_barcode || "—"}</p>
              </div>
              <div className="flex items-end justify-between">
                <span className="font-mono font-bold text-slate-900">{peso(p.retail_price)}</span>
                <span className={`text-[11px] font-medium ${p.is_low_stock ? "text-amber-700" : "text-slate-500"}`}>
                  {p.stock_quantity} left
                </span>
              </div>
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full py-16 text-center text-sm text-slate-400">
              {products.length === 0 ? "No products yet. Add items in Inventory." : "No products match your search."}
            </div>
          )}
        </div>
      </div>

      <div className="lg:col-span-2">
        <div className="lg:sticky lg:top-24 bg-white border border-slate-200 rounded-xl flex flex-col max-h-[calc(100vh-8rem)]">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-heading font-semibold text-slate-900">Current Sale</h3>
            <span className="text-xs text-slate-500">{cart.length} item{cart.length !== 1 ? "s" : ""}</span>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[120px]">
            {cart.length === 0 && <p className="text-sm text-slate-400 text-center py-8">Cart is empty — scan or tap products.</p>}
            {cart.map((c) => (
              <div key={c.product_id} data-testid="pos-cart-item" className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{c.name}</p>
                  <p className="font-mono text-xs text-slate-500">{peso(c.unit_price)}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button data-testid="pos-cart-qty-decrement" onClick={() => setQty(c.product_id, c.quantity - 1)}
                    className="w-8 h-8 rounded-md border border-slate-200 flex items-center justify-center hover:bg-slate-50">
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <span className="w-8 text-center font-mono text-sm font-semibold">{c.quantity}</span>
                  <button data-testid="pos-cart-qty-increment" onClick={() => setQty(c.product_id, c.quantity + 1)}
                    className="w-8 h-8 rounded-md border border-slate-200 flex items-center justify-center hover:bg-slate-50">
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
                <span className="w-20 text-right font-mono text-sm font-semibold">{peso(c.unit_price * c.quantity)}</span>
                <button data-testid="pos-cart-remove-item" onClick={() => setQty(c.product_id, 0)}
                  className="text-slate-300 hover:text-red-600 transition-colors">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="border-t border-slate-100 p-4 space-y-2 text-sm">
            <div className="flex justify-between text-slate-600"><span>Subtotal</span><span className="font-mono">{peso(totals.subtotal)}</span></div>
            <div className="flex justify-between items-center">
              <button data-testid="pos-discount-trigger-btn" onClick={() => { setDiscountDraft({ type: discount.type, value: discount.value || "" }); setDiscountOpen(true); }}
                className="flex items-center gap-1.5 text-xs font-medium text-blue-700 hover:underline">
                <Tag className="h-3.5 w-3.5" /> {discount.value > 0 ? "Edit discount" : "Add discount"}
              </button>
              <span className="font-mono text-slate-600">-{peso(totals.discountAmount)}</span>
            </div>
            {bir && (
              <div className="flex items-center justify-between">
                <Label className="text-xs text-slate-500">Tax treatment</Label>
                <Select value={totals.treatment} onValueChange={(v) => { setTaxTreatment(v); if (v !== "vat_exempt") setSeniorPwd(false); }} disabled={seniorPwd}>
                  <SelectTrigger className="w-40 h-8 text-xs" data-testid="pos-tax-treatment-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vatable">VATable</SelectItem>
                    {(taxCfg?.allow_vat_exempt ?? true) && <SelectItem value="vat_exempt">VAT-Exempt</SelectItem>}
                    {(taxCfg?.allow_zero_rated ?? true) && <SelectItem value="zero_rated">Zero-Rated</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
            )}
            {bir && (
              <div data-testid="pos-vat-breakdown-panel" className="rounded-lg bg-slate-50 border border-slate-100 p-3 space-y-1 text-xs">
                {totals.vatable > 0 && <div className="flex justify-between text-slate-500"><span>VATable Sales</span><span className="font-mono">{peso(totals.vatable)}</span></div>}
                {totals.vat > 0 && <div className="flex justify-between text-slate-500"><span>VAT ({Math.round((taxCfg?.vat_rate ?? 0.12) * 100)}%)</span><span className="font-mono">{peso(totals.vat)}</span></div>}
                {totals.exempt > 0 && <div className="flex justify-between text-slate-500"><span>VAT-Exempt Sales</span><span className="font-mono">{peso(totals.exempt)}</span></div>}
                {totals.zero > 0 && <div className="flex justify-between text-slate-500"><span>Zero-Rated Sales</span><span className="font-mono">{peso(totals.zero)}</span></div>}
                {seniorPwd && <div className="flex justify-between text-emerald-700"><span>Senior/PWD ({Math.round((taxCfg?.senior_discount_rate ?? 0.2) * 100)}%)</span><span className="font-mono">-{peso(totals.seniorDiscount)}</span></div>}
                <button data-testid="pos-senior-pwd-discount-btn" onClick={() => setSeniorPwd(!seniorPwd)}
                  className={`mt-1 flex items-center gap-1.5 text-xs font-semibold ${seniorPwd ? "text-emerald-700" : "text-slate-500 hover:text-slate-800"}`}>
                  <BadgePercent className="h-3.5 w-3.5" /> Senior Citizen / PWD {seniorPwd ? "applied ✓" : "discount"}
                </button>
              </div>
            )}
            <div className="flex justify-between items-baseline pt-2">
              <span className="font-semibold text-slate-900">Total</span>
              <span className="font-mono font-bold text-2xl tracking-tight text-slate-950" data-testid="pos-cart-total">{peso(totals.total)}</span>
            </div>
            <Button
              data-testid="pos-checkout-btn"
              className="w-full h-12 text-base bg-emerald-600 hover:bg-emerald-700 mt-2"
              disabled={cart.length === 0}
              onClick={handleCharge}
            >
              Charge {peso(totals.total)}
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={discountOpen} onOpenChange={setDiscountOpen}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle className="font-heading">Order Discount</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <RadioGroup value={discountDraft.type} onValueChange={(v) => setDiscountDraft({ ...discountDraft, type: v })} className="flex gap-4">
              <div className="flex items-center gap-2"><RadioGroupItem value="amount" id="d-amt" /><Label htmlFor="d-amt">₱ Off</Label></div>
              <div className="flex items-center gap-2"><RadioGroupItem value="percent" id="d-pct" /><Label htmlFor="d-pct">% Off</Label></div>
            </RadioGroup>
            <Input type="number" min="0" value={discountDraft.value} data-testid="pos-discount-value-input"
              onChange={(e) => setDiscountDraft({ ...discountDraft, value: e.target.value })} placeholder="Value" />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => { setDiscount({ type: "amount", value: 0 }); setDiscountOpen(false); }}>Clear</Button>
              <Button className="flex-1 bg-slate-900" data-testid="pos-discount-apply-btn"
                onClick={() => { setDiscount({ type: discountDraft.type, value: Number(discountDraft.value) || 0 }); setDiscountOpen(false); }}>
                Apply
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Payment</DialogTitle></DialogHeader>
          <Tabs value={paymentMethod} onValueChange={setPaymentMethod}>
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="cash" data-testid="pos-payment-cash-tab">Cash</TabsTrigger>
              <TabsTrigger value="ewallet" data-testid="pos-payment-ewallet-tab">E-Wallet</TabsTrigger>
              <TabsTrigger value="card" data-testid="pos-payment-card-tab">Card</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label className="text-xs">Customer / Buyer (optional)</Label>
              <Select value={buyerId} onValueChange={setBuyerId}>
                <SelectTrigger className="h-9" data-testid="pos-customer-select"><SelectValue placeholder="Walk-in customer" /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.customer_id} value={c.customer_id}>{c.last_name}, {c.first_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="grid grid-cols-2 gap-2">
                <Input className="h-8 text-xs" placeholder="Buyer name" value={buyerForm.name} onChange={(e) => setBuyerForm({ ...buyerForm, name: e.target.value })} data-testid="pos-buyer-name-input" />
                <Input className="h-8 text-xs font-mono" placeholder="Buyer TIN" value={buyerForm.tin} onChange={(e) => setBuyerForm({ ...buyerForm, tin: e.target.value })} data-testid="pos-buyer-tin-input" />
                <Input className="h-8 text-xs" placeholder="Address" value={buyerForm.address} onChange={(e) => setBuyerForm({ ...buyerForm, address: e.target.value })} />
                <Input className="h-8 text-xs" placeholder="Business name" value={buyerForm.business_name} onChange={(e) => setBuyerForm({ ...buyerForm, business_name: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-between items-baseline">
              <span className="text-sm text-slate-500">Amount due</span>
              <span className="font-mono font-bold text-2xl">{peso(totals.total)}</span>
            </div>
            {paymentMethod === "cash" && (
              <div className="space-y-3">
                <Input type="number" min="0" data-testid="pos-tendered-amount-input" value={tendered}
                  onChange={(e) => setTendered(e.target.value)} placeholder="Tendered amount" className="h-12 font-mono text-lg" />
                <div className="flex gap-2">
                  {[100, 500, 1000].map((b) => (
                    <Button key={b} variant="outline" size="sm" onClick={() => setTendered(String(b))} className="font-mono">₱{b}</Button>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => setTendered(totals.total.toFixed(2))}>Exact</Button>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Change</span>
                  <span className={`font-mono font-bold ${change < 0 ? "text-red-600" : "text-emerald-700"}`}>{peso(Math.max(change, 0))}</span>
                </div>
              </div>
            )}
            {paymentMethod === "ewallet" && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
                <div className="text-center space-y-2">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(`QRPH|MATTEUSMX|${totals.total.toFixed(2)}|PHP`)}`}
                    alt="QRPh payment QR" className="mx-auto rounded border border-slate-200 bg-white p-2" width={140} height={140}
                    data-testid="pos-qrph-image"
                  />
                  <p className="text-xs text-slate-500">Customer scans the store QRPh code and pays {peso(totals.total)} via GCash / Maya.</p>
                  <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 leading-relaxed">
                    All e-wallet payments are subject to manual approval — the customer sends proof of payment after paying.
                  </p>
                </div>
                <Input className="h-9 font-mono text-sm" placeholder="Payment reference number" value={ewRef} onChange={(e) => setEwRef(e.target.value)} data-testid="pos-ewallet-ref-input" />
              </div>
            )}
            {paymentMethod === "card" && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 text-center space-y-1">
                <p className="text-sm font-medium text-slate-700">Card Terminal</p>
                <p className="text-xs text-slate-500">Charge {peso(totals.total)} on the connected card reader</p>
              </div>
            )}
            <Button
              data-testid="pos-complete-order-btn"
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700"
              disabled={submitting
                || (paymentMethod === "cash" && !offline && (!tendered || Number(tendered) < totals.total))
                || (paymentMethod === "ewallet" && !ewRef.trim() && !offline)}
              onClick={completeOrder}
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Complete Order
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={rxOpen} onOpenChange={setRxOpen}>
        <DialogContent className="max-w-sm" data-testid="pos-rx-confirm-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Pill className="h-5 w-5 text-blue-600" /> Prescription Verification</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600 leading-relaxed">
              The following item{rxItems.length > 1 ? "s require" : " requires"} a prescription:
            </p>
            <ul className="rounded-lg bg-blue-50 border border-blue-100 p-3 space-y-1">
              {rxItems.map((c) => <li key={c.product_id} className="text-sm font-medium text-blue-900">• {c.name}</li>)}
            </ul>
            <p className="text-sm text-slate-600">Has the customer's prescription been verified?</p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setRxOpen(false)} data-testid="pos-rx-cancel-btn">Cancel</Button>
              <Button className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="pos-rx-confirm-btn"
                onClick={() => { setRxOpen(false); setCheckoutOpen(true); }}>
                Verified — Proceed
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ReceiptDialog sale={receipt} open={!!receipt} onOpenChange={(o) => !o && setReceipt(null)} />
    </div>
  );
}
